import React, { useEffect, useState } from 'react'
import { SimulatorConfig, ConversationStats } from '../types/chat'
import { TemporaryMessage } from './TemporaryMessage'
import { Wifi, WifiOff, RotateCcw, RefreshCw, Download, Play, Bug, Wrench } from 'lucide-react'

interface ConfigPanelProps {
  config: SimulatorConfig
  isConnected: boolean
  isDebugMode: boolean
  conversationStats?: ConversationStats
  onConfigChange: (config: SimulatorConfig) => void
  onDebugModeChange: (enabled: boolean) => void
  onResetSession: () => void
  onCheckConnection: () => void
  onLoadConversation?: () => void
  onStartConversation?: () => void
  onOpenAdminPanel?: () => void
  conversationHistory?: {
    isLoading: boolean
    error: string | null
    history: any | null
    clearError: () => void
  }
}

const ConfigPanel: React.FC<ConfigPanelProps> = ({
  config,
  isConnected,
  isDebugMode,
  conversationStats,
  onConfigChange,
  onDebugModeChange,
  onResetSession,
  onCheckConnection,
  onLoadConversation,
  onStartConversation,
  onOpenAdminPanel,
  conversationHistory
}) => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleConfigChange = (field: keyof SimulatorConfig, value: string) => {
    onConfigChange({
      ...config,
      [field]: value
    })

    if (field === 'phoneNumber') {
      localStorage.setItem('lastPhoneNumber', value)
    }
  }

  useEffect(() => {
    const savedPhone = localStorage.getItem('lastPhoneNumber')
    if (savedPhone && savedPhone !== config.phoneNumber && !config.phoneNumber) {
      onConfigChange({
        ...config,
        phoneNumber: savedPhone
      })
    }
  }, [])

  useEffect(() => {
    if (conversationHistory?.history && conversationHistory.history.messages) {
      const messageCount = conversationHistory.history.messages.length
      if (messageCount === 0) {
        setSuccessMessage('No se encontró historial para este número')
      } else {
        setSuccessMessage(`✅ Historial cargado: ${messageCount} mensajes`)
      }
    }
  }, [conversationHistory?.history])

  const formatCost = (cost?: number) => {
    if (!cost) return '€0.00'
    return `€${cost.toFixed(4)}`
  }

  const formatTokens = (tokens?: number) => {
    if (!tokens) return '0'
    return tokens.toLocaleString()
  }

  return (
    <div className="config-panel">
      <div className="config-header">
        <h2 className="config-title">Configuración</h2>
        {onOpenAdminPanel && (
          <button 
            onClick={onOpenAdminPanel} 
            className="admin-btn"
            title="Admin Panel"
          >
            <Wrench size={16} />
          </button>
        )}
      </div>

      <div className="config-section">        
        <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
          {isConnected ? 'Servidor OK' : 'Sin conexión'}
        </div>

        <div className="input-group">
          <label>
            <input
              type="checkbox"
              checked={isDebugMode}
              onChange={(e) => onDebugModeChange(e.target.checked)}
            />
            <Bug size={14} />
            Modo Debug MCP
          </label>
        </div>

        <div className="input-group">
          <label>Número de Teléfono</label>
          <input
            type="text"
            value={config.phoneNumber}
            onChange={(e) => handleConfigChange('phoneNumber', e.target.value)}
            placeholder="647123456"
          />
        </div>
        
        {onStartConversation && (
          <button onClick={onStartConversation} className="btn btn-primary">
            <Play size={14} />
            Iniciar Conversación
          </button>
        )}
        
        {onLoadConversation && (
          <button 
            onClick={onLoadConversation} 
            className="btn btn-secondary"
            disabled={conversationHistory?.isLoading}
          >
            <Download size={14} />
            {conversationHistory?.isLoading ? 'Cargando...' : 'Cargar Conversación'}
          </button>
        )}

        <TemporaryMessage
          message={conversationHistory?.error || null}
          type="error"
          onClear={conversationHistory?.clearError}
        />

        <TemporaryMessage
          message={successMessage}
          type={successMessage?.includes('No se encontró') ? 'error' : 'success'}
          onClear={() => setSuccessMessage(null)}
        />
      </div>

      <div className="config-section">
        <h3>Estadísticas</h3>
        
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-value">{formatTokens(conversationStats?.totalTokens)}</div>
            <div className="stat-label">Tokens usados</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-value">{formatCost(conversationStats?.totalCost)}</div>
            <div className="stat-label">Coste total</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-value">{conversationStats?.inputTokens || 0}</div>
            <div className="stat-label">Input Tokens</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-value">{conversationStats?.outputTokens || 0}</div>
            <div className="stat-label">Output Tokens</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-value">{conversationStats?.messageCount || 0}</div>
            <div className="stat-label">Mensajes</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-label">Etiquetas</div>
            <div className="tags-container">
              {conversationStats?.tags?.length ? (
                conversationStats.tags.map((tag, index) => (
                  <span key={index} className={`tag ${tag}`}>
                    {tag}
                  </span>
                ))
              ) : (
                <span className="tag">Sin etiquetas</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="config-section">
        <h3>Acciones</h3>
        
        <button onClick={onResetSession} className="btn btn-danger">
          <RotateCcw size={14} />
          Resetear Sesión
        </button>

        <button onClick={onCheckConnection} className="btn btn-info">
          <RefreshCw size={14} />
          Verificar Conexión
        </button>
      </div>

      <style>{`
        .config-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .config-title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .admin-btn {
          padding: 6px 10px;
          background: #f5f5f5;
          border: 1px solid #ddd;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          color: #666;
          font-size: 13px;
          transition: all 0.2s;
        }

        .admin-btn:hover {
          background: #e0e0e0;
          border-color: #ccc;
          color: #333;
        }

        .admin-btn:active {
          transform: scale(0.95);
        }
      `}</style>
    </div>
  )
}

export default ConfigPanel
